import React, { createContext, useMemo } from 'react'

import { getTotalItemsFromPaginatedResult } from 'components/now-running/now-running-utils'
import useAuth from 'hooks/use-auth'
import { useGetAttacksActiveWithPagination } from 'queries/attacks/use-get-attacks-active-with-pagination'
import { useGetFailureFlagsExperimentsActiveWithPagination } from 'queries/failure-flags/use-get-failure-flags-experiments-active-with-pagination'
import { useReliabilityTestsActiveWithPagination } from 'queries/reliability-tests/use-reliability-tests-active-paginated'
import { useGetScenariosActiveWithPagination } from 'queries/scenarios/use-get-scenarios-active-with-pagination'
import {
  attacksActivePagedGet,
  failureFlagsExperimentsActiveGet,
  reliabilityTestsActivePagedGet,
  scenariosActivePagedGet
} from 'utils/endpoints-access'
import { ms } from 'utils/time/millisecond'

import type { UseInfiniteQueryResult } from 'react-query/types/react/types'
import type { AttacksActivePagedResponse } from 'types/api/attacks/active/paged'
import type { FailureFlagExperiment } from 'types/api/failure-flags/failure-flags'
import type { ReliabilityTestsActivePagedResponse } from 'types/api/reliability-tests/active'
import type { ScenariosActivePagedResponse } from 'types/api/scenarios/active'
import type { FetchFunc } from 'types/util'

export interface NowRunningContextValues {
  activeAttacksWithPaginationFetchState: UseInfiniteQueryResult<AttacksActivePagedResponse>
  activeReliabilityTestsWithPaginationFetchState: UseInfiniteQueryResult<ReliabilityTestsActivePagedResponse>
  activeScenariosWithPaginationFetchState: UseInfiniteQueryResult<ScenariosActivePagedResponse>
  activeFailureFlagExperiments: FailureFlagExperiment[]
  isFetchingActiveFailureFlagExperiments: boolean
  hasErrorActiveFailureFlagExperiments: boolean
  refetchActiveFailureFlagExperiments: FetchFunc
  isLoading: boolean
  totalActiveAttacks: number
  totalActiveReliabilityTests: number
  totalActiveScenarioRuns: number
  totalActiveTests: number
}

export const DEFAULT_NOW_RUNNING_CONTEXT_VALUES: NowRunningContextValues = {
  activeAttacksWithPaginationFetchState:
    {} as UseInfiniteQueryResult<AttacksActivePagedResponse>,
  activeReliabilityTestsWithPaginationFetchState:
    {} as UseInfiniteQueryResult<ReliabilityTestsActivePagedResponse>,
  activeScenariosWithPaginationFetchState:
    {} as UseInfiniteQueryResult<ScenariosActivePagedResponse>,
  activeFailureFlagExperiments: [],
  isFetchingActiveFailureFlagExperiments: false,
  hasErrorActiveFailureFlagExperiments: false,
  refetchActiveFailureFlagExperiments: async () => {},
  isLoading: true,
  totalActiveAttacks: 0,
  totalActiveReliabilityTests: 0,
  totalActiveScenarioRuns: 0,
  totalActiveTests: 0
}

export const NowRunningContext = createContext<NowRunningContextValues>(
  DEFAULT_NOW_RUNNING_CONTEXT_VALUES
)

export function useNowRunningContext() {
  return React.useContext(NowRunningContext)
}

const REFETCH_INTERVAL = ms.seconds(10)
const STALE_TIME = REFETCH_INTERVAL

export const NowRunningContextProvider: React.FC = ({ children }) => {
  const {
    currentTeamId,
    hasEndpointAccess,
    hasFailureFlags,
    hasFaultInjection,
    hasReliabilityManagement
  } = useAuth()

  const activeReliabilityTestsWithPaginationFetchState =
    useReliabilityTestsActiveWithPagination({
      enabled:
        hasReliabilityManagement &&
        hasEndpointAccess(reliabilityTestsActivePagedGet, {
          teamId: currentTeamId
        }),
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    })

  const activeScenariosWithPaginationFetchState =
    useGetScenariosActiveWithPagination({
      enabled:
        hasFaultInjection &&
        hasEndpointAccess(scenariosActivePagedGet, {
          teamId: currentTeamId
        }),
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    })

  const activeAttacksWithPaginationFetchState =
    useGetAttacksActiveWithPagination({
      enabled:
        hasFaultInjection &&
        hasEndpointAccess(attacksActivePagedGet, { teamId: currentTeamId }),
      staleTime: STALE_TIME,
      refetchInterval: REFETCH_INTERVAL
    })

  const {
    data: activeFailureFlagExperiments = [],
    isFetching: isFetchingActiveFailureFlagExperiments,
    isLoading: isLoadingActiveFailureFlagExperiments,
    isError: hasErrorActiveFailureFlagExperiments,
    refetch: refetchActiveFailureFlagExperiments
  } = useGetFailureFlagsExperimentsActiveWithPagination({
    enabled:
      hasFailureFlags &&
      hasEndpointAccess(failureFlagsExperimentsActiveGet, {
        teamId: currentTeamId
      }),
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL
  })

  const isLoading =
    activeAttacksWithPaginationFetchState.isLoading ||
    activeScenariosWithPaginationFetchState.isLoading ||
    activeReliabilityTestsWithPaginationFetchState.isLoading ||
    isLoadingActiveFailureFlagExperiments

  /**
   * Total active test counts
   */
  const totalActiveReliabilityTests = getTotalItemsFromPaginatedResult(
    activeReliabilityTestsWithPaginationFetchState
  )
  const totalActiveScenarioRuns = getTotalItemsFromPaginatedResult(
    activeScenariosWithPaginationFetchState
  )
  const totalActiveAttacks = getTotalItemsFromPaginatedResult(
    activeAttacksWithPaginationFetchState
  )

  const totalActiveTests = useMemo(() => {
    if (isLoading) {
      return 0
    }

    const totalActiveFailureFlagExperiments =
      activeFailureFlagExperiments.length

    return (
      totalActiveAttacks +
      totalActiveScenarioRuns +
      totalActiveReliabilityTests +
      totalActiveFailureFlagExperiments
    )
  }, [
    activeFailureFlagExperiments.length,
    isLoading,
    totalActiveAttacks,
    totalActiveReliabilityTests,
    totalActiveScenarioRuns
  ])

  /**
   * Memoized context values
   */
  const value = useMemo<NowRunningContextValues>(() => {
    return {
      activeAttacksWithPaginationFetchState,
      activeReliabilityTestsWithPaginationFetchState,
      activeScenariosWithPaginationFetchState,
      activeFailureFlagExperiments,
      isFetchingActiveFailureFlagExperiments,
      hasErrorActiveFailureFlagExperiments,
      refetchActiveFailureFlagExperiments,
      isLoading,
      totalActiveAttacks,
      totalActiveReliabilityTests,
      totalActiveScenarioRuns,
      totalActiveTests
    }
  }, [
    activeAttacksWithPaginationFetchState,
    activeReliabilityTestsWithPaginationFetchState,
    activeScenariosWithPaginationFetchState,
    activeFailureFlagExperiments,
    isFetchingActiveFailureFlagExperiments,
    hasErrorActiveFailureFlagExperiments,
    refetchActiveFailureFlagExperiments,
    isLoading,
    totalActiveAttacks,
    totalActiveReliabilityTests,
    totalActiveScenarioRuns,
    totalActiveTests
  ])

  return (
    <NowRunningContext.Provider value={value}>
      {children}
    </NowRunningContext.Provider>
  )
}
